import type { JobEntity } from '@/types/job.types';
import { ROUTES } from '@/app/router/routes';

/**
 * Format experience months as "2Y 3M - 5Y".
 * Mirrors Flutter GenericHelpers.getMonthsToYears()
 */
export function formatExperience(from?: number | null, to?: number | null): string {
  const fromMonths = from ?? 0;
  const toMonths = to ?? 0;
  if (fromMonths === 0 && toMonths === 0) return '0M 0Y';

  const toText = (months: number): string => {
    if (months <= 0) return '';
    const years = Math.floor(months / 12);
    const rest = Math.floor(months % 12);
    return rest > 0 ? `${years}Y ${rest}M` : `${years}Y`;
  };

  const fromText = toText(fromMonths);
  const toTextValue = toText(toMonths);
  if (fromText && toTextValue) return `${fromText} - ${toTextValue}`;
  return fromText || toTextValue;
}

type Translate = (key: string) => string;

/**
 * Relative posted date, e.g. "3 days ago".
 * Mirrors Flutter GenericHelpers.postedDays() — callers prefix it with "Posted".
 */
export function formatPostedDate(
  startDate?: number | string | null,
  t: Translate = (key) => key,
): string {
  if (startDate == null || startDate === '') return t('N/A');
  const posted =
    typeof startDate === 'number' || /^\d+$/.test(startDate)
      ? new Date(Number(startDate))
      : new Date(startDate);
  if (isNaN(posted.getTime())) return t('N/A');

  const diffMs = Date.now() - posted.getTime();
  const days = Math.floor(diffMs / 86_400_000);
  const hours = Math.floor(diffMs / 3_600_000);
  const minutes = Math.floor(diffMs / 60_000);
  const unit = (count: number, one: string, many: string) =>
    `${count} ${count === 1 ? t(one) : t(many)}`;

  if (days > 365) return unit(Math.floor(days / 365), 'year ago', 'years ago');
  if (days > 30) return unit(Math.floor(days / 30), 'month ago', 'months ago');
  if (days > 7) return unit(Math.floor(days / 7), 'week ago', 'weeks ago');
  if (days > 0) return unit(days, 'day ago', 'days ago');
  if (hours > 0) return unit(hours, 'hour ago', 'hours ago');
  if (minutes > 0) return unit(minutes, 'minute ago', 'minutes ago');
  return t('just now');
}

/**
 * Calendar date, e.g. "12 Sep, 2026".
 * Mirrors Flutter GenericHelpers.getFormattedDate() (DateFormat 'dd MMM, yyyy' in the app language).
 */
export function formatDate(value?: number | null, locale?: string): string {
  if (value == null) return 'N/A';
  const date = new Date(value);
  if (isNaN(date.getTime())) return 'N/A';
  const day = String(date.getDate()).padStart(2, '0');
  let month: string;
  try {
    month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
  } catch {
    month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
  }
  return `${day} ${month}, ${date.getFullYear()}`;
}

/**
 * Get the first job location display string.
 */
export function formatJobLocation(entity: JobEntity): string {
  if (entity.jobLocation?.length && entity.jobLocation[0].fqLocationName) {
    return entity.jobLocation[0].fqLocationName;
  }
  return '';
}

/**
 * Build the job share URL.
 * Mirrors Flutter: ${origin}/jobs/${displayId}
 */
export function buildJobShareUrl(displayId: string): string {
  return `${window.location.origin}${ROUTES.JOBS}/${displayId}`;
}

/**
 * Share to a social platform.
 * Mirrors Flutter _shareToSocialPlatform from job_list_view.dart
 */
export function shareToSocialPlatform(
  platform: string,
  shareUrl: string,
  shareText = '🚀 Exciting career opportunities for you. Check out our latest job openings and join a team.',
): void {
  let url = '';

  switch (platform.toLowerCase()) {
    case 'linkedin':
      url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
      break;
    case 'x':
    case 'twitter':
      url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
      break;
    case 'whatsapp':
      url = `https://wa.me/?text=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;
      break;
    case 'email':
      url = `mailto:?subject=${encodeURIComponent('Career Opportunities for you')}&body=${encodeURIComponent(`${shareText}\n\nCheck it out: ${shareUrl}`)}`;
      break;
    default:
      return;
  }

  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Copy text to clipboard.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Format string — capitalize first letter, replace underscores.
 * Mirrors Flutter GenericHelpers.formatString()
 */
export function formatString(value?: string): string {
  if (!value) return '';
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
